<template>
  <!-- Single root required by the vue/no-multiple-template-root rule; Teleport lives inside. -->
  <div>
  <div class="flex flex-col flex-1 w-full min-h-[calc(100vh-6rem)] px-5 sm:px-6 md:px-8 py-8 bg-gray-50 dark:bg-slate-800/30">
    <div class="max-w-2xl w-full mx-auto">

      <!-- Header -->
      <div class="mb-8">
        <NuxtLink to="/contacts" class="inline-flex items-center gap-1 text-sm font-medium text-gray-500 dark:text-slate-400 hover:text-gray-900 dark:hover:text-slate-100 transition-colors mb-3">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/></svg>
          Back to Contacts
        </NuxtLink>
        <h1 class="text-3xl font-bold tracking-tight text-gray-900 dark:text-slate-100">Linked Devices</h1>
        <p class="mt-2 text-sm text-gray-500 dark:text-slate-400 max-w-xl">
          Connect your phone contacts to this browser securely. Your contacts are encrypted
          end-to-end — Beesphone servers never see plaintext contact data.
        </p>
      </div>

      <!-- Loading -->
      <div v-if="!isReady" class="flex flex-col items-center justify-center py-20 space-y-4">
        <div class="animate-spin rounded-full h-10 w-10 border-b-2 border-primary" />
        <span class="text-sm text-gray-500 dark:text-slate-400 font-medium">Initializing secure connection...</span>
      </div>

      <template v-else>

        <!-- ── UNPAIRED STATE: QR Code ── -->
        <div v-if="contactStore.status === 'unpaired'" class="space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-500">
          <div class="bg-white dark:bg-slate-900 rounded-3xl shadow-sm border border-gray-200 dark:border-slate-700 p-8 sm:p-10 flex flex-col items-center">
            
            <div class="w-full max-w-xs space-y-6 mb-8">
              <div class="flex flex-col gap-5">
                <div class="flex items-start gap-4">
                  <div class="w-8 h-8 shrink-0 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-sm">1</div>
                  <p class="text-sm text-gray-700 dark:text-slate-300 leading-relaxed mt-1">Open the Beesphone mobile app, then go to <strong class="font-semibold text-gray-900 dark:text-slate-100">Settings → Linked Devices</strong>.</p>
                </div>
                <div class="flex items-start gap-4">
                  <div class="w-8 h-8 shrink-0 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-sm">2</div>
                  <p class="text-sm text-gray-700 dark:text-slate-300 leading-relaxed mt-1">Tap <strong class="font-semibold text-gray-900 dark:text-slate-100">Link New Device</strong> and scan this QR code.</p>
                </div>
              </div>
            </div>

            <!-- QR Code -->
            <div class="relative rounded-2xl overflow-hidden border-2 border-gray-100 dark:border-slate-800 shadow-sm p-4 bg-white dark:bg-slate-900 mb-6">
              <canvas ref="qrCanvas" class="w-56 h-56" />
              <div v-if="isGeneratingQr" class="absolute inset-0 flex flex-col items-center justify-center bg-white dark:bg-slate-900/90 backdrop-blur-sm space-y-3">
                <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
                <span class="text-xs font-semibold text-gray-600 dark:text-slate-400">Generating</span>
              </div>
            </div>

            <p class="text-xs text-gray-400 text-center max-w-sm">
              This QR code is unique to this browser and expires when you close this tab.
              Scan it again next time from the Beesphone app.
            </p>
          </div>
        </div>

        <!-- ── PAIRED / SYNCING STATE ── -->
        <div v-else class="space-y-6 animate-in fade-in duration-300">

          <!-- Status bar -->
          <div
            class="flex items-center gap-3 px-5 py-4 rounded-2xl shadow-sm border border-gray-100 dark:border-slate-800"
            :class="statusClass"
          >
            <span class="relative flex h-3.5 w-3.5">
              <span
                v-if="contactStore.status === 'syncing'"
                class="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75"
                :class="statusDotClass"
              />
              <span class="relative inline-flex rounded-full h-full w-full" :class="statusDotClass" />
            </span>
            <span class="text-sm font-semibold tracking-wide">{{ statusLabel }}</span>
          </div>

          <!-- Device info card -->
          <div class="bg-white dark:bg-slate-900 rounded-3xl shadow-sm border border-gray-200 dark:border-slate-700 overflow-hidden">
            <div class="flex flex-col sm:flex-row sm:items-center gap-5 px-6 py-6 pb-5">
              <div class="shrink-0 p-4 rounded-2xl bg-gray-50 dark:bg-slate-800 flex items-center justify-center border border-gray-100 dark:border-slate-800">
                <svg class="w-8 h-8 text-gray-600 dark:text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="1.5"
                    d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
              </div>
              <div class="flex-1 space-y-1">
                <div class="flex items-center gap-3">
                  <h3 class="text-base font-bold text-gray-900 dark:text-slate-100">This browser</h3>
                  <span class="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-bold">Active</span>
                </div>
                <div class="text-sm text-gray-500 dark:text-slate-400 flex flex-wrap items-center gap-x-2">
                  <span>Linked {{ linkedAtFormatted }}</span>
                  <span v-if="contactStore.lastSyncedAt" class="text-gray-300 hidden sm:inline">&bull;</span>
                  <span v-if="contactStore.lastSyncedAt">
                    Synced {{ lastSyncedFormatted }}
                  </span>
                </div>
              </div>
            </div>

            <!-- Contact count summary -->
            <div v-if="contactStore.contacts.length > 0" class="px-6 py-4 bg-gray-50 dark:bg-slate-800/50 border-t border-gray-100 dark:border-slate-800 flex items-center justify-between">
              <div class="flex items-center gap-2">
                <svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"/></svg>
                <span class="text-sm font-medium text-gray-600 dark:text-slate-400">
                  {{ contactStore.contacts.length }} contacts synced
                </span>
              </div>
              <NuxtLink to="/contacts" class="text-sm text-primary hover:text-primary-dark transition-colors font-semibold flex items-center gap-1">
                View contacts
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/></svg>
              </NuxtLink>
            </div>
          </div>

          <!-- Actions -->
          <div class="flex flex-col sm:flex-row gap-3 pt-2">
            <button
              class="flex-1 flex items-center justify-center gap-2 px-5 py-3 rounded-2xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm font-semibold text-gray-700 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-800 hover:border-gray-300 transition-all shadow-sm active:scale-[0.98] disabled:opacity-50 disabled:pointer-events-none"
              :disabled="contactStore.status === 'syncing'"
              @click="handleManualSync"
            >
              <svg class="w-4 h-4" :class="{ 'animate-spin': contactStore.status === 'syncing' }" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
              {{ contactStore.status === 'syncing' ? 'Syncing...' : 'Sync Now' }}
            </button>

            <button
              class="flex-1 flex items-center justify-center gap-2 px-5 py-3 rounded-2xl border border-red-200 bg-red-50 text-sm font-semibold text-red-600 hover:bg-red-100 hover:border-red-300 transition-all shadow-sm active:scale-[0.98]"
              @click="handleUnlink"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
                />
              </svg>
              Unlink This Browser
            </button>
          </div>

          <!-- Error message -->
          <div
            v-if="contactStore.status === 'error' && contactStore.errorMessage"
            class="rounded-2xl bg-red-50 border border-red-200 px-5 py-4 text-sm font-medium text-red-700 shadow-sm flex items-start gap-3"
          >
            <svg class="w-5 h-5 shrink-0 text-red-500 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
            {{ contactStore.errorMessage }}
          </div>
        </div>

      </template>
    </div>
  </div>

  <!-- Unlink confirmation modal -->
  <Teleport to="body">
    <div
      v-if="showUnlinkConfirm"
      class="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/40 backdrop-blur-sm px-4 p-4 sm:p-0 animate-in fade-in duration-200"
    >
      <div class="bg-white dark:bg-slate-900 rounded-3xl shadow-2xl max-w-sm w-full p-6 space-y-6 animate-in zoom-in-95 duration-200">
        <div class="space-y-2">
          <h2 class="text-xl font-bold text-gray-900 dark:text-slate-100">Unlink this browser?</h2>
          <p class="text-sm text-gray-500 dark:text-slate-400 leading-relaxed">
            Your contacts will no longer sync safely to this browser. You can re-link any time by scanning a new QR code
            from the Beesphone mobile app.
          </p>
        </div>
        <div class="flex gap-3">
          <button
            class="flex-1 px-4 py-3 rounded-2xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm font-semibold text-gray-700 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors"
            @click="showUnlinkConfirm = false"
          >Cancel</button>
          <button
            class="flex-1 px-4 py-3 rounded-2xl bg-red-600 text-sm font-semibold text-white hover:bg-red-700 transition-colors shadow-sm"
            @click="confirmUnlink"
          >Unlink</button>
        </div>
      </div>
    </div>
  </Teleport>
  </div>
</template>

<script setup lang="ts">
import QRCode from 'qrcode';
import type { Database } from 'firebase/database';
import { useContactStore } from '~/stores/contactStore';
import { useNavbarInfo } from '~/stores/useNabvarInfo';

definePageMeta({ ssr: false });

const navbar = useNavbarInfo();
navbar.changeInfo('Contacts', []);

useHead({ title: 'Linked Devices' });

// ── Auth & Firebase ───────────────────────────────────────────────────────────

const { $auth, $rtdb } = useNuxtApp();
const db = $rtdb as unknown as Database;
const uid = ($auth as ReturnType<typeof import('firebase/auth').getAuth> | undefined)?.currentUser?.uid;

// ── Store ─────────────────────────────────────────────────────────────────────

const contactStore = useContactStore();
const isReady = ref(false);
const isGeneratingQr = ref(false);
const showUnlinkConfirm = ref(false);
const qrCanvas = ref<HTMLCanvasElement | null>(null);

// ── Lifecycle ─────────────────────────────────────────────────────────────────

onMounted(async () => {
  if (!uid) return navigateTo('/auth/signin');

  await contactStore.initialize(uid, db);
  isReady.value = true;

  // If unpaired, generate a fresh keypair and render the QR code.
  if (contactStore.status === 'unpaired') {
    await generateAndRenderQr();
  }
});

// ── QR Code ───────────────────────────────────────────────────────────────────

async function generateAndRenderQr() {
  isGeneratingQr.value = true;
  try {
    const qrContent = await contactStore.preparePairing(uid!, db);
    // Wait for the canvas ref to be mounted (it becomes visible after isReady = true).
    await nextTick();
    if (qrCanvas.value) {
      await QRCode.toCanvas(qrCanvas.value, qrContent, {
        width: 224,
        margin: 2,
        color: { dark: '#111827', light: '#ffffff' },
      });
    }
  } finally {
    isGeneratingQr.value = false;
  }
}

// ── Actions ───────────────────────────────────────────────────────────────────

async function handleManualSync() {
  if (!uid) return;
  await contactStore.requestSync(uid, db);
}

function handleUnlink() {
  showUnlinkConfirm.value = true;
}

async function confirmUnlink() {
  showUnlinkConfirm.value = false;
  if (!uid) return;
  await contactStore.unlink(uid, db);
  // Regenerate QR after unlinking.
  await nextTick();
  await generateAndRenderQr();
}

// ── Derived / Display Helpers ─────────────────────────────────────────────────

const statusClass = computed(() => ({
  'bg-green-50 text-green-700':
    contactStore.status === 'paired',
  'bg-blue-50 text-blue-700':
    contactStore.status === 'syncing',
  'bg-red-50 text-red-700':
    contactStore.status === 'error',
}));

const statusDotClass = computed(() => ({
  'bg-green-500': contactStore.status === 'paired',
  'bg-blue-500': contactStore.status === 'syncing',
  'bg-red-500': contactStore.status === 'error',
}));

const statusLabel = computed(() => {
  switch (contactStore.status) {
    case 'paired': return 'Connected & up to date';
    case 'syncing': return 'Syncing contacts…';
    case 'error': return 'Sync error';
    default: return '';
  }
});

const linkedAtFormatted = computed(() => {
  if (!contactStore.linkedAt) return '';
  return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' }).format(
    new Date(contactStore.linkedAt),
  );
});

const lastSyncedFormatted = computed(() => {
  if (!contactStore.lastSyncedAt) return '';
  const diff = Date.now() - contactStore.lastSyncedAt;
  if (diff < 60_000) return 'just now';
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  return `${Math.floor(diff / 3_600_000)}h ago`;
});
</script>
